/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { FormattedMessage } from '@jutro/locale';
import React from 'react';
import QuestionSetsViewModel from './QuestionSetsViewModel';
import { isQuestionTypeYear } from './aspects/Validity';
import appConfig from 'app-config';

/**
 * Retrieves questionSet from QuestionSets metadata
 * @param {string} qsName
 * @param {object} metadata
 * @throws Will throw an error if questionSet does not exist in QuestionSets metadata
 * @returns {object} - The single question from questionSets metadata
 */
function getQuestionSet(qsName, metadata) {
    const questionSet = metadata[qsName];
    if (!questionSet || !questionSet.orderedQuestions) {
        throw new Error(`There is no question set loaded with the name ${qsName}`);
    }
    return questionSet;
}

const today = new Date();
function getBirthYearOptions() {
    const minValue = today.getFullYear() - 150;
    const maxValue = today.getFullYear();
    const birthYearOptions = [];
    for (let i = maxValue; i >= minValue; i -= 1) {
        birthYearOptions.push({
            displayName: i,
            id: i
        });
    }
    return birthYearOptions;
}

const isQB = appConfig.portalCode === "QB"
export default class QuestionSetsParser {
    /**
     * @throws Will throw an error if questionSetsSubmissionValue is not provided
     * @param {object} questionSetsSubmissionValue
     * @param {object} metadata
     * @param {function} translator
     * @param {array} questionsToShowML - MetLife field to allow filtering of questions on front end. Required as questions from the same question set are dynmically displayed on different pages. if no value provided no filtering occurs
     * @param {function} isFieldDefaultedByHVQ
     * @param (function) checkReadOnlyAnswer - function to allow calling components to determine if an answer should be read-only
     */
    constructor(questionSetsSubmissionValue, metadata, translator, questionsToShowML, showCompRaterSpacing, isFieldDefaultedByHVQ, currentStep, checkReadOnlyAnswer, getChoicesToFilter) {
        // eslint-disable-next-line no-underscore-dangle
        this._viewModel = {};
        this.translator = translator;
        this.isFieldDefaultedByHVQ = isFieldDefaultedByHVQ;
        this.showCompRaterSpacing = showCompRaterSpacing;
        this.currentStep = currentStep;
        this.checkReadOnlyAnswer = checkReadOnlyAnswer;
        this.getChoicesToFilter = getChoicesToFilter;

        if (
            !_.isObject(questionSetsSubmissionValue)
            && !_.isArray(questionSetsSubmissionValue)
        ) {
            throw new Error('A model object is required for storing answers');
        }

        if (_.isArray(questionSetsSubmissionValue)) {
            questionSetsSubmissionValue.forEach((submissionValue) => {
                // eslint-disable-next-line no-underscore-dangle
                this._createViewModel(submissionValue, metadata, questionsToShowML);
            });
        } else {
            // eslint-disable-next-line no-underscore-dangle
            this._createViewModel(questionSetsSubmissionValue, metadata, questionsToShowML);
        }
    }

    /**
     * Creates viewModel based on questionSet
     * @param {object} submissionValue - The submission value object
     * @param {string} submissionValue.code - QuestionSet code
     * @param {array} submissionValue.answers - QuestionSet available answers
     * @param {object} metadata
     * @returns {object} - viewModel
     */
    // eslint-disable-next-line consistent-return, no-underscore-dangle
    _createViewModel(submissionValue, metadata, questionsToShowML) {
        // if view model already created then just return that object
        if (submissionValue.aspects
            && submissionValue.questions
            && _.isArray(submissionValue.questions)) {
            return submissionValue;
        }

        const questionSet = getQuestionSet(submissionValue.code, metadata);
        questionSet.orderedQuestions.sort((a, b) => a.order - b.order)
            .forEach((question) => {
                if (!_.isUndefined(questionsToShowML)
                    && !questionsToShowML.includes(question.code)) {
                    return;
                }

                const answer = submissionValue.answers[question.code];
                if (answer !== undefined) {
                    if (typeof answer === 'boolean') {
                        submissionValue.answers[question.code] = answer ? 'true' : 'false';
                    }
                    if (question.questionType === 'Integer' && answer === null) {
                        submissionValue.answers[question.code] = '';
                    }
                    // eslint-disable-next-line no-underscore-dangle
                    this._viewModel[question.code] = new QuestionSetsViewModel(
                        question, submissionValue, this.translator
                    );
                    const values = question.choices.map((choice) => {
                        return {
                            code: choice.choiceCode,
                            name: this.translator({
                                id: choice.displayKey,
                                defaultMessage: choice.choiceCode
                            })
                        };
                    });
                    // eslint-disable-next-line no-underscore-dangle
                    _.extend(this._viewModel[question.code].aspects.availableValues, values);
                }
            });
        // eslint-disable-next-line no-underscore-dangle
        this._viewModel = _.merge({}, this._viewModel, submissionValue);
    }

    /**
     * Returns the viewModel created via parser
     * @returns {ViewModel} - The viewModel type based on questionSets
     */
    get viewModel() {
        // eslint-disable-next-line no-underscore-dangle
        return this._viewModel;
    }

    isDefaultedFieldByCode = (model) => {
        return !!this.isFieldDefaultedByHVQ && this.isFieldDefaultedByHVQ(model.code);
    };

    /**
     * Returns presentation metadata based on viewModel and questionSets properties
     * @returns {object} - Presentation metadata used to render questionSets fields
     */
    get presentationMetadata() {
        const pageContent = [];
        // eslint-disable-next-line no-underscore-dangle
        _.forOwn(this._viewModel, (questionVM, key) => {
            // eslint-disable-next-line no-underscore-dangle
            if (QuestionSetsParser.filterQuestionSets(key) && this._viewModel[key]) {
                // eslint-disable-next-line no-underscore-dangle
                const field = this._viewModel[key];
                const metadataContent = {
                    id: key,
                    type: 'field',
                    componentProps: {
                        label: { id: field.label, defaultMessage: field.label },
                        layout: 'reversed',
                        path: key,
                        required : field?.question?.required // TO Make Questions as required if they are required in PD.
                    }
                };

                if (field.aspects.inputCtrlType === 'typelist') {
                    metadataContent.componentProps.placeholder = {
                        id: 'jutro-components.widgets.inputs.DropdownSelectField.placeholder',
                        defaultMessage: 'Select'
                    };
                    metadataContent.componentProps.alwaysShowPlaceholder = true;
                    metadataContent.componentProps.searchable = true;
                    metadataContent.componentProps.className= isQB ? 'question-set-parser-dropdown flex-column-qs-qb radioButtonQnB' : 'question-set-parser-dropdown flex-column-qs';
                }

                if (field.aspects.inputCtrlType === 'text' || field.aspects.inputCtrlType === 'number') {
                    metadataContent.componentProps.controlClassName = 'col-max-500';
                    metadataContent.componentProps.className = isQB ? 'flex-column-qs-qb radioButtonQnB' : 'flex-column-qs';
                    if((this?.currentStep && this?.currentStep?.mlStepName === "compPaValidateInfo") && (field.question.code == 'TenureOriginalEffDate_Ext' || field.question.code == 'CompanyPolicyNumber_Ext')){
                        field.question.required = true;
                    }
                }

                if (field.aspects.inputCtrlType === 'boolean') {
                    metadataContent.component = 'radiobutton';
                    metadataContent.componentProps.className = isQB ? 'flex-column-qs-qb radioButtonQnB' : 'flex-column-qs';
                    metadataContent.componentProps.controlClassName = 'radio-button-qs'
                }

                if (field.aspects.inputCtrlType === 'date') {
                    metadataContent.componentProps.displayFormat = 'vshort';
                    metadataContent.componentProps.className = isQB ? 'flex-column-qs-qb radioButtonQnB' : 'flex-column-qs';
                }

                // QuestionSetDirective.isAnimalBirthYearQuestion
                if (isQuestionTypeYear(questionVM.question)) {
                    metadataContent.component = 'dropdownselect';
                    metadataContent.componentProps.availableValues = getBirthYearOptions();
                    metadataContent.componentProps.placeholder = {
                        id: 'jutro-components.widgets.inputs.DropdownSelectField.placeholder',
                        defaultMessage: 'Select'
                    };
                    metadataContent.componentProps.alwaysShowPlaceholder = true;
                    metadataContent.componentProps.searchable = true;
                }

                if (!_.isUndefined(this.getChoicesToFilter)) {
                    try {
                        let returnValues = field.aspects.availableValues;
                        const listsToFilter = this.getChoicesToFilter();

                        listsToFilter.forEach((list) => {
                                if (list.fieldName === field?.question?.code) {
                                    list.filterList.forEach((item) => {
                                        returnValues = returnValues?.filter((val) => val.code !== item);
                                    });
                                }
                            }
                        );
                        metadataContent.componentProps.availableValues = returnValues;
                    } catch (error) {
                        console.log(error);
                    }
                }

                // Metlife Tooltip
                if (!_.isUndefined(questionVM.question.helpTexts_ML) && questionVM.question.helpTexts_ML.length > 0) {
                    let msg = '';
                    if (questionVM.question.helpTexts_ML.length > 1) {
                        questionVM.question.helpTexts_ML.forEach((helpText) => {
                            msg += this.translator(
                                {
                                    id: helpText.displayKey,
                                    defaultMessage: helpText.displayKey
                                }
                            )
                            msg += '{break}'
                        });
                    } else {
                        msg = this.translator(
                            {
                                id: questionVM.question.helpTexts_ML[0].displayKey,
                                defaultMessage: questionVM.question.helpTexts_ML[0].displayKey
                            }
                        )
                    }

                    metadataContent.componentProps.tooltip = {
                        placement: 'bottom',
                        text: this.translator(
                            {
                                id: questionVM.question.code,
                                defaultMessage: msg
                            },
                            {
                                break: (<><br /><br /></>)
                            }
                        ),
                        className: 'tooltip-qs'
                    };
                }

                // Metlife hvq warning message
                let hvqWarningMetadataContent;
                if(this.isDefaultedFieldByCode(questionVM.question)) {
                    hvqWarningMetadataContent = {
                        id: `${key}Warning`,
                        type: 'element',
                        component: 'InlineNotification',
                        componentProps: {
                            type: 'warning',
                            isDismissable: false,
                            message: {
                                id: "ml.questionset.hvq.Verify or update above information",
                                defaultMessage: "Verify or update above information"
                            },
                            className: 'hvq-inline-notification'
                        }
                    };
                }

                /** Farmers */
                if(this.isReadOnly(key) || (this.checkReadOnlyAnswer && this.checkReadOnlyAnswer(key))){
                    metadataContent.componentProps.disabled = true;
                     metadataContent.componentProps.readOnly = true;                    
                }

                pageContent.push(metadataContent);
                if(hvqWarningMetadataContent) {
                    pageContent.push(hvqWarningMetadataContent);
                }
            }
        });
        return {
            content: [
                {
                    id: 'questionSetGridId',
                    type: 'container',
                    component: 'div',
                    contentLayout: { component: 'grid' },
                    content: pageContent,
                    componentProps: {
                        className: this.showCompRaterSpacing ? 'compspacing' : null
                    }
                }
            ]
        };
    }

    /**
     * Search for questions only, ignoring answers and code
     * @param {string} key
     * @returns {boolean}
     */
    static filterQuestionSets(key) {
        return key !== 'answers' && key !== 'code' && key !== 'mlDateAnswers' && key !== "readOnlyAnswers_Ext";
    }

    isReadOnly(key) {
        return _.get(this._viewModel, 'readOnlyAnswers_Ext', []).includes(key);
    }
}